import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Eye, CalendarClock, Bell, Activity, GitCompareArrows, Trash2, Link2, FileText, Video, History, Printer } from "lucide-react";
import { api, apiErr, fileUrl } from "../api";
import { DETAIL_GROUPS } from "../fields";
import { PanelPage, Header, PageTitle, FullLoader, StatusPill, prettyDate, calcAge, WhatsAppIcon } from "../ui";
import NextAppointmentModal from "../components/NextAppointmentModal";
import FollowUpModal from "../components/FollowUpModal";
import { copyText, handleSendResult } from "../components/SendResult";

export default function PatientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [appts, setAppts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([api.get(`/patients/${id}`), api.get("/appointments", { params: { patient_id: id } }),
        api.get("/reminders/history", { params: { patient_id: id } })]);
      setP(a.data); setAppts(b.data); setLogs(c.data);
    } catch (e) { toast.error(apiErr(e, "No se pudo cargar")); nav("/dashboard"); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  if (!p) return <PanelPage><Header /><FullLoader /></PanelPage>;

  const delUpdate = async (uid) => {
    if (!window.confirm("¿Eliminar este seguimiento?")) return;
    try { await api.delete(`/patients/${id}/updates/${uid}`); toast.success("Seguimiento eliminado"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const resend = async (channel) => {
    try { const { data } = await api.post(`/appointments/${p.next_appointment.id}/notify`, { channel }); handleSendResult(data, channel); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const na = p.next_appointment;
  const updates = p.updates || [];
  const files = [...(p.lab_media || []), ...(p.ekg_media || []), ...(p.eco_media || [])];
  const age = p.edad || calcAge(p.fecha_nacimiento);

  return (
    <PanelPage>
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm pn-sub mb-4 pn-noprint"><ArrowLeft size={15} /> Pacientes</Link>
        <PageTitle eyebrow="Ficha clínica" title={p.nombre}
          sub={<span className="flex flex-wrap gap-x-3 gap-y-1">
            {p.cedula && <span>CI {p.cedula}</span>}{age && <span>{age} años</span>}{p.sexo && <span>{p.sexo}</span>}
            {p.tipo_sangre && <span>Sangre {p.tipo_sangre}</span>}{p.titulo_diagnostico && <span className="pn-accent">{p.titulo_diagnostico}</span>}
            {p.enfermedades_cronicas && <span className="pn-brand font-semibold"><Activity size={12} className="inline" /> Crónico · control cada 3 meses</span>}
          </span>}>
          <span className="pn-noprint flex flex-wrap gap-2">
            <button onClick={() => setModal("appt")} className="pn-btn pn-btn-primary"><CalendarClock size={16} /> Próxima consulta</button>
            <button onClick={() => setModal("follow")} className="pn-btn pn-btn-soft"><Activity size={16} /> Seguimiento</button>
            <Link to={`/pacientes/${id}/comparar`} className="pn-btn pn-btn-ghost"><GitCompareArrows size={16} /> Comparar</Link>
            <Link to={`/visor/${id}`} className="pn-btn pn-btn-ghost"><Eye size={16} /> Visor</Link>
            <Link to={`/pacientes/${id}/editar`} className="pn-btn pn-btn-ghost"><Pencil size={16} /> Editar</Link>
            <button onClick={() => window.print()} className="pn-icon-btn" title="Imprimir ficha"><Printer size={16} /></button>
          </span>
        </PageTitle>

        {na ? (
          <div className="pn-card rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-3">
            <Bell size={18} className="pn-brand" />
            <span className="pn-ink text-sm">Próxima consulta: <b>{prettyDate(na.fecha, true)}{na.hora ? ` · ${na.hora}` : ""}</b>{na.servicio ? ` · ${na.servicio}` : ""}{na.nota ? <span className="pn-sub"> — {na.nota}</span> : ""}</span>
            <StatusPill status={na.status} />
            {na.status === "reprogramar" && <span className="text-xs" style={{ color: "var(--warn)" }}>Pide: {na.requested_fecha || "otra fecha"}{na.nota_paciente ? ` — “${na.nota_paciente}”` : ""}</span>}
            <span className="ml-auto flex gap-1.5 pn-noprint">
              <button onClick={() => resend("whatsapp")} className="pn-icon-btn" title="Reenviar por WhatsApp" style={{ color: "#16A34A" }}><WhatsAppIcon /></button>
              <button onClick={() => resend("email")} className="pn-btn pn-btn-ghost !py-1.5 text-xs" disabled={!p.email}>Reenviar email</button>
              <button onClick={() => copyText(`${window.location.origin}/cita/${na.confirm_token}`, "Enlace de confirmación copiado")} className="pn-icon-btn" title="Copiar enlace de confirmación"><Link2 size={15} /></button>
            </span>
          </div>
        ) : (
          <div className="pn-card rounded-2xl p-4 mb-6 text-sm pn-sub flex items-center gap-2"><CalendarClock size={16} /> Sin próxima cita. Usa “Próxima consulta” para agendarla y enviársela.</div>
        )}

        <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
          <div className="space-y-5">
            {DETAIL_GROUPS.map((g) => {
              const rows = g.items.filter(([k]) => p[k]);
              if (!rows.length) return null;
              return (
                <section key={g.group} className="pn-card rounded-2xl overflow-hidden">
                  <h2 className="px-5 py-3 font-bold pn-ink pn-card2 border-b pn-line">{g.group}</h2>
                  <dl className="divide-y" style={{ borderColor: "var(--line)" }}>
                    {rows.map(([k, label]) => (
                      <div key={k} className="grid sm:grid-cols-[210px_1fr] gap-1 px-5 py-3 border-t pn-line first:border-t-0">
                        <dt className="text-sm pn-sub">{label}</dt>
                        <dd className="text-sm pn-ink whitespace-pre-wrap break-words">{k.startsWith("fecha") ? prettyDate(p[k]) : p[k]}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              );
            })}
            {files.length > 0 && (
              <section className="pn-card rounded-2xl p-5">
                <h2 className="font-bold pn-ink mb-3">Archivos</h2>
                <div className="flex flex-wrap gap-3">
                  {files.map((m, i) => (
                    <a key={m.path + i} href={fileUrl(m.path)} target="_blank" rel="noreferrer" className="w-28 rounded-xl overflow-hidden pn-card2 border pn-line text-center">
                      <div className="h-20 flex items-center justify-center">
                        {m.type === "image" ? <img src={fileUrl(m.path)} alt={m.filename} className="w-full h-full object-cover" /> : m.type === "video" ? <Video className="pn-accent" /> : <FileText className="pn-brand" />}
                      </div>
                      <div className="text-[10px] pn-sub truncate px-1.5 py-1">{m.filename}</div>
                    </a>
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside className="space-y-5">
            <section className="pn-card rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold pn-ink flex items-center gap-2"><Activity size={16} className="pn-brand" /> Seguimientos ({updates.length})</h2>
                {updates.length > 0 && <Link to={`/pacientes/${id}/comparar`} className="text-xs pn-accent font-semibold pn-noprint">Comparar →</Link>}
              </div>
              {updates.length === 0 ? <p className="text-sm pn-sub">Sin seguimientos. Registra cada control para comparar la evolución.</p> : (
                <ol className="space-y-2">
                  {updates.map((u, i) => (
                    <li key={u.id} className="pn-card2 rounded-xl p-3 flex justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold pn-ink">#{i + 1} · {prettyDate(u.fecha)}</div>
                        {u.nota && <div className="text-xs pn-sub">{u.nota}</div>}
                        {u.cuadro_clinico && <div className="text-xs pn-muted line-clamp-2">{u.cuadro_clinico}</div>}
                      </div>
                      <button onClick={() => delUpdate(u.id)} className="pn-muted hover:pn-brand pn-noprint" title="Eliminar"><Trash2 size={14} /></button>
                    </li>
                  ))}
                </ol>
              )}
            </section>
            <section className="pn-card rounded-2xl p-5">
              <h2 className="font-bold pn-ink mb-3 flex items-center gap-2"><CalendarClock size={16} className="pn-brand" /> Citas ({appts.length})</h2>
              {appts.length === 0 ? <p className="text-sm pn-sub">Sin citas registradas.</p> : (
                <ul className="space-y-1.5">{[...appts].reverse().map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="pn-ink">{prettyDate(a.fecha)}{a.hora ? ` · ${a.hora}` : ""}</span><StatusPill status={a.status} />
                  </li>
                ))}</ul>
              )}
            </section>
            <section className="pn-card rounded-2xl p-5">
              <h2 className="font-bold pn-ink mb-3 flex items-center gap-2"><History size={16} className="pn-brand" /> Envíos ({logs.length})</h2>
              {logs.length === 0 ? <p className="text-sm pn-sub">Sin envíos todavía.</p> : (
                <ul className="space-y-1.5">{logs.slice(0, 12).map((l) => (
                  <li key={l.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="pn-sub">{new Date(l.created_at).toLocaleDateString("es-EC")} · {l.channel}</span><StatusPill status={l.status} />
                  </li>
                ))}</ul>
              )}
            </section>
          </aside>
        </div>
      </main>
      {modal === "appt" && <NextAppointmentModal patient={p} onClose={() => setModal(null)} onSaved={load} />}
      {modal === "follow" && <FollowUpModal patient={p} onClose={() => setModal(null)} onSaved={load} />}
    </PanelPage>
  );
}
